import React, { useState, useMemo } from 'react';
import { Plus, Search, Edit2, Trash2, Calendar, MapPin, Clock, Users, User, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useEvents, useEventMutations, useLogMutations } from '../../hooks/useConvex';
import { useAuth } from '../../contexts/AuthContext';
import { Event, EventCategory } from '../../types';
import { ImageUpload } from '../../components/ui/ImageUpload';
import { sortEventsByTime } from '../../utils/eventUtils';
import { Id } from '../../../convex/_generated/dataModel';

export const EventsPage: React.FC = () => {
    const { user, adminRole, assignedEvents } = useAuth();
    const eventsData = useEvents();
    const { create, update, remove } = useEventMutations();
    const { logAdminActivity } = useLogMutations();
    const loading = eventsData === undefined;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<EventCategory | 'all'>('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditManagerModal, setIsEditManagerModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // Transform Convex data to Event type
    const events = useMemo(() => {
        if (!eventsData) return [];
        return eventsData.map((e: Record<string, unknown>) => ({
            ...e,
            event_id: e._id,
        })) as Event[];
    }, [eventsData]);

    const [formData, setFormData] = useState<Partial<Event>>({
        name: '',
        sub_event_name: '',
        category: 'tech',
        venue: { name: '' },
        day: 'Day 1',
        schedule: {
            day1: { start: '', end: '' },
            day2: { start: '', end: '' }
        },
        description: '',
        guidelines: '',
        cover_image_url: '',
        rules: [],
        max_participants: 1,
        is_active: true,
        event_manager: { name: '', phone: '' }
    });

    const [managerData, setManagerData] = useState({ name: '', phone: '' });

    const isEventManager = adminRole === 'event_manager';
    const canManageEvent = (eventId: string) => {
        if (adminRole === 'supreme_admin' || adminRole === 'admin') return true;
        if (isEventManager && assignedEvents.includes(eventId)) return true;
        return false;
    };

    const canCreateDelete = adminRole === 'supreme_admin' || adminRole === 'admin';

    const handleSaveEvent = async () => {
        if (!formData.name || !formData.category) {
            alert('Please fill in required fields');
            return;
        }

        try {
            setActionLoading(true);
            if (selectedEvent) {
                if (!canManageEvent(selectedEvent.event_id)) {
                    alert('You do not have permission to edit this event');
                    return;
                }
                await update({
                    eventId: selectedEvent.event_id as Id<'events'>,
                    name: formData.name,
                    subEventName: formData.sub_event_name,
                    category: formData.category as 'tech' | 'science' | 'logic' | 'creative',
                    venue: formData.venue,
                    day: formData.day as 'Day 1' | 'Day 2' | 'Both' | undefined,
                    schedule: formData.schedule,
                    description: formData.description,
                    guidelines: formData.guidelines,
                    coverImageUrl: formData.cover_image_url,
                    rules: formData.rules,
                    maxParticipants: formData.max_participants,
                    isActive: formData.is_active,
                    eventManager: formData.event_manager,
                });
                await logAdminActivity({
                    email: user?.email || '',
                    action: 'update_event',
                    details: `Updated event: ${formData.name}`,
                });
            } else {
                if (!canCreateDelete) {
                    alert('You do not have permission to create events');
                    return;
                }
                await create({
                    name: formData.name || '',
                    subEventName: formData.sub_event_name,
                    category: (formData.category || 'tech') as 'tech' | 'science' | 'logic' | 'creative',
                    venue: formData.venue,
                    day: (formData.day || 'Day 1') as 'Day 1' | 'Day 2' | 'Both',
                    schedule: formData.schedule || { time: '' },
                    description: formData.description || '',
                    guidelines: formData.guidelines,
                    coverImageUrl: formData.cover_image_url,
                    rules: formData.rules || [],
                    maxParticipants: formData.max_participants || 1,
                    isActive: formData.is_active !== false,
                    eventManager: formData.event_manager,
                    tagline: '',
                    overview: formData.description || '',
                    eligibility: {},
                    scoring: {},
                    materials: {},
                    contactPerson: {},
                });
                await logAdminActivity({
                    email: user?.email || '',
                    action: 'create_event',
                    details: `Created event: ${formData.name}`,
                });
            }
            setIsModalOpen(false);
            setFormData({
                name: '',
                sub_event_name: '',
                category: 'tech',
                venue: { name: '' },
                schedule: { time: '' },
                description: '',
                guidelines: '',
                cover_image_url: '',
                rules: [],
                max_participants: 1,
                is_active: true,
                event_manager: { name: '', phone: '' }
            });
            setSelectedEvent(null);
        } catch (error: any) {
            alert(error.message || 'Failed to save event');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteEvent = async (eventId: string, eventName: string) => {
        if (!canCreateDelete) return;
        if (!window.confirm(`Are you sure you want to delete "${eventName}"? This action cannot be undone.`)) {
            return;
        }

        setDeletingId(eventId);
        try {
            await remove({ eventId: eventId as Id<'events'> });
            await logAdminActivity({
                email: user?.email || '',
                action: 'delete_event',
                details: `Deleted event: ${eventName}`,
            });
        } catch (error: any) {
            console.error('Error deleting event:', error);
            alert(error.message || 'Failed to delete event');
        } finally {
            setDeletingId(null);
        }
    };

    const handleUpdateManager = async () => {
        if (!selectedEvent) return;
        if (!canManageEvent(selectedEvent.event_id)) {
            alert('You do not have permission to manage this event');
            return;
        }

        try {
            setActionLoading(true);
            await update({
                eventId: selectedEvent.event_id as Id<'events'>,
                eventManager: managerData
            });

            await logAdminActivity({
                email: user?.email || '',
                action: 'update_event_manager',
                details: `Updated manager for: ${selectedEvent.name}`,
            });

            setIsEditManagerModal(false);
            setManagerData({ name: '', phone: '' });
            setSelectedEvent(null);
        } catch (error: any) {
            alert(error.message || 'Failed to update manager');
        } finally {
            setActionLoading(false);
        }
    };

    const filteredEvents = events.filter(event => {
        const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleExportPDF = () => {
        const day1Events = sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 1'), 'day1');
        const day2Events = sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 2'), 'day2');
        const bothDaysEvents = sortEventsByTime(filteredEvents.filter(e => e.day === 'Both'), 'both');

        const data: string[][] = [];

        if (day1Events.length > 0) {
            data.push(['--- DAY 1 EVENTS ---', '', '', '', '', '', '']);
            day1Events.forEach(event => {
                data.push([
                    event.name,
                    event.category,
                    'Day 1',
                    `${event.schedule?.day1?.start || 'TBA'} - ${event.schedule?.day1?.end || 'TBA'}`,
                    (event.venue as { name?: string })?.name || 'TBA',
                    (event.max_participants || 0).toString(),
                    event.event_manager?.name || 'Unassigned'
                ]);
            });
        }

        if (day2Events.length > 0) {
            data.push(['--- DAY 2 EVENTS ---', '', '', '', '', '', '']);
            day2Events.forEach(event => {
                data.push([
                    event.name,
                    event.category,
                    'Day 2',
                    `${event.schedule?.day2?.start || 'TBA'} - ${event.schedule?.day2?.end || 'TBA'}`,
                    (event.venue as { name?: string })?.name || 'TBA',
                    (event.max_participants || 0).toString(),
                    event.event_manager?.name || 'Unassigned'
                ]);
            });
        }

        if (bothDaysEvents.length > 0) {
            data.push(['--- MULTI-DAY EVENTS ---', '', '', '', '', '', '']);
            bothDaysEvents.forEach(event => {
                data.push([
                    event.name,
                    event.category,
                    'Both',
                    `D1: ${event.schedule?.day1?.start || 'TBA'} | D2: ${event.schedule?.day2?.start || 'TBA'}`,
                    (event.venue as { name?: string })?.name || 'TBA',
                    (event.max_participants || 0).toString(),
                    event.event_manager?.name || 'Unassigned'
                ]);
            });
        }

        pdfService.generatePDF({
            title: 'Events List (Sorted by Schedule)',
            filename: 'events_list',
            columns: ['Name', 'Category', 'Day', 'Schedule', 'Venue', 'Max Participants', 'Manager'],
            data: data,
            orientation: 'landscape',
        });
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'tech': return 'border-l-4 border-l-blue-500';
            case 'science': return 'border-l-4 border-l-green-500';
            case 'logic': return 'border-l-4 border-l-purple-500';
            case 'creative': return 'border-l-4 border-l-amber-500';
            default: return '';
        }
    };

    const day1Events = sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 1'), 'day1');
    const day2Events = sortEventsByTime(filteredEvents.filter(e => e.day === 'Day 2'), 'day2');
    const multiDayEvents = sortEventsByTime(filteredEvents.filter(e => e.day === 'Both'), 'both');

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Events</h1>
                            <p className="text-sm text-white/30 mt-0.5">Manage symposium events</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="secondary"
                            onClick={handleExportPDF}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export
                        </Button>
                        {canCreateDelete && (
                            <Button variant="primary" onClick={() => {
                                setSelectedEvent(null);
                                setFormData({
                                    name: '',
                                    category: 'tech',
                                    venue: { name: '' },
                                    day: 'Day 1',
                                    schedule: {
                                        day1: { start: '', end: '' },
                                        day2: { start: '', end: '' }
                                    },
                                    description: '',
                                    guidelines: '',
                                    cover_image_url: '',
                                    rules: [],
                                    max_participants: 1,
                                    is_active: true,
                                    event_manager: { name: '', phone: '' }
                                });
                                setIsModalOpen(true);
                            }}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Event
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filter Section */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-white text-sm placeholder:text-white/25 focus:border-brand/30 focus:outline-none transition-all"
                    />
                </div>
                <div className="flex gap-2 flex-wrap">
                    {(['all', 'tech', 'science', 'logic', 'creative'] as const).map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                                selectedCategory === cat
                                    ? 'bg-brand/[0.08] text-brand border-brand/20'
                                    : 'bg-white/[0.03] text-white/35 border-white/[0.06] hover:text-white/60 hover:border-white/[0.1]'
                            }`}
                        >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader size="lg" />
                </div>
            ) : (
                <>
                    {/* Day 1 Section */}
                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-white/[0.06]" />
                        <span className="text-xs font-medium uppercase tracking-widest text-white/20">Day 1</span>
                        <div className="flex-1 h-px bg-white/[0.06]" />
                    </div>

                    {day1Events.length === 0 ? (
                        <div className="text-center py-12">
                            <Calendar className="w-10 h-10 mx-auto mb-3 text-white/10" />
                            <p className="text-sm text-white/20">No Day 1 events found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {day1Events.map((event) => (
                                <Card
                                    key={event.event_id}
                                    className={`group relative ${getCategoryColor(event.category)}`}
                                    hoverEffect
                                >
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-base font-semibold text-white">{event.name}</h3>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {canManageEvent(event.event_id) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedEvent(event);
                                                            setFormData(event);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {canCreateDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteEvent(event.event_id, event.name)}
                                                        className="h-7 w-7 p-0 text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                                        isLoading={deletingId === event.event_id}
                                                        disabled={!!deletingId}
                                                    >
                                                        {!deletingId && <Trash2 className="w-3.5 h-3.5" />}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <MapPin className="w-3.5 h-3.5" />
                                                <span>{(event.venue as { name?: string })?.name || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span>{event.schedule?.day1?.start || 'TBA'} - {event.schedule?.day1?.end || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Users className="w-3.5 h-3.5" />
                                                <span>Max {event.max_participants} per team</span>
                                            </div>
                                        </div>

                                        <div className="pt-3 mt-3 border-t border-white/[0.04] flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-white/[0.04] flex items-center justify-center">
                                                    <User className="w-3.5 h-3.5 text-white/30" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-white/20">Manager</p>
                                                    <p className="text-xs text-white/50">{event.event_manager?.name || 'Unassigned'}</p>
                                                </div>
                                            </div>
                                            {canManageEvent(event.event_id) && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedEvent(event);
                                                        setManagerData(event.event_manager || { name: '', phone: '' });
                                                        setIsEditManagerModal(true);
                                                    }}
                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Day 2 Section */}
                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-white/[0.06]" />
                        <span className="text-xs font-medium uppercase tracking-widest text-white/20">Day 2</span>
                        <div className="flex-1 h-px bg-white/[0.06]" />
                    </div>

                    {day2Events.length === 0 ? (
                        <div className="text-center py-12">
                            <Calendar className="w-10 h-10 mx-auto mb-3 text-white/10" />
                            <p className="text-sm text-white/20">No Day 2 events found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {day2Events.map((event) => (
                                <Card
                                    key={event.event_id}
                                    className={`group relative ${getCategoryColor(event.category)}`}
                                    hoverEffect
                                >
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-base font-semibold text-white">{event.name}</h3>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {canManageEvent(event.event_id) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedEvent(event);
                                                            setFormData(event);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {canCreateDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteEvent(event.event_id, event.name)}
                                                        className="h-7 w-7 p-0 text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                                        isLoading={deletingId === event.event_id}
                                                        disabled={!!deletingId}
                                                    >
                                                        {!deletingId && <Trash2 className="w-3.5 h-3.5" />}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <MapPin className="w-3.5 h-3.5" />
                                                <span>{(event.venue as { name?: string })?.name || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span>{event.schedule?.day2?.start || 'TBA'} - {event.schedule?.day2?.end || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Users className="w-3.5 h-3.5" />
                                                <span>Max {event.max_participants} per team</span>
                                            </div>
                                        </div>

                                        <div className="pt-3 mt-3 border-t border-white/[0.04] flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-white/[0.04] flex items-center justify-center">
                                                    <User className="w-3.5 h-3.5 text-white/30" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-white/20">Manager</p>
                                                    <p className="text-xs text-white/50">{event.event_manager?.name || 'Unassigned'}</p>
                                                </div>
                                            </div>
                                            {canManageEvent(event.event_id) && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedEvent(event);
                                                        setManagerData(event.event_manager || { name: '', phone: '' });
                                                        setIsEditManagerModal(true);
                                                    }}
                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Multi-Day Section */}
                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-white/[0.06]" />
                        <span className="text-xs font-medium uppercase tracking-widest text-white/20">Multi-Day</span>
                        <div className="flex-1 h-px bg-white/[0.06]" />
                    </div>

                    {multiDayEvents.length === 0 ? (
                        <div className="text-center py-12">
                            <Calendar className="w-10 h-10 mx-auto mb-3 text-white/10" />
                            <p className="text-sm text-white/20">No multi-day events found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {multiDayEvents.map((event) => (
                                <Card
                                    key={event.event_id}
                                    className={`group relative ${getCategoryColor(event.category)}`}
                                    hoverEffect
                                >
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-base font-semibold text-white">{event.name}</h3>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {canManageEvent(event.event_id) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedEvent(event);
                                                            setFormData(event);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {canCreateDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteEvent(event.event_id, event.name)}
                                                        className="h-7 w-7 p-0 text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                                        isLoading={deletingId === event.event_id}
                                                        disabled={!!deletingId}
                                                    >
                                                        {!deletingId && <Trash2 className="w-3.5 h-3.5" />}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <MapPin className="w-3.5 h-3.5" />
                                                <span>{(event.venue as { name?: string })?.name || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span>D1: {event.schedule?.day1?.start || 'TBA'} | D2: {event.schedule?.day2?.start || 'TBA'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Users className="w-3.5 h-3.5" />
                                                <span>Max {event.max_participants} per team</span>
                                            </div>
                                        </div>

                                        <div className="pt-3 mt-3 border-t border-white/[0.04] flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-white/[0.04] flex items-center justify-center">
                                                    <User className="w-3.5 h-3.5 text-white/30" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-white/20">Manager</p>
                                                    <p className="text-xs text-white/50">{event.event_manager?.name || 'Unassigned'}</p>
                                                </div>
                                            </div>
                                            {canManageEvent(event.event_id) && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedEvent(event);
                                                        setManagerData(event.event_manager || { name: '', phone: '' });
                                                        setIsEditManagerModal(true);
                                                    }}
                                                    className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Add/Edit Event Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedEvent ? "Edit Event" : "Add New Event"}
                size="xl"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left Column */}
                    <div className="space-y-4">
                        <Input
                            label="Event Name"
                            value={formData.name || ''}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter event name"
                            fullWidth
                        />

                        <div>
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Category
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value as EventCategory })}
                                className="w-full bg-surface border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm focus:border-brand/30 focus:outline-none appearance-none"
                            >
                                <option value="tech">Technology</option>
                                <option value="science">Science</option>
                                <option value="logic">Logic & Math</option>
                                <option value="creative">Creative</option>
                            </select>
                        </div>

                        <Input
                            label="Venue"
                            value={(formData.venue as { name?: string })?.name || ''}
                            onChange={(e) => setFormData({ ...formData, venue: { ...formData.venue, name: e.target.value } })}
                            placeholder="e.g., Main Auditorium"
                            fullWidth
                        />

                        <div>
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description || ''}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full bg-surface border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm min-h-[120px] focus:border-brand/30 focus:outline-none resize-none"
                                placeholder="Event description..."
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Event Day
                            </label>
                            <select
                                value={formData.day || 'Day 1'}
                                onChange={(e) => setFormData({ ...formData, day: e.target.value as 'Day 1' | 'Day 2' | 'Both' })}
                                className="w-full bg-surface border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm focus:border-brand/30 focus:outline-none appearance-none"
                            >
                                <option value="Day 1">Day 1</option>
                                <option value="Day 2">Day 2</option>
                                <option value="Both">Both Days</option>
                            </select>
                        </div>

                        {(formData.day === 'Day 1' || formData.day === 'Both') && (
                            <div className="grid grid-cols-2 gap-3">
                                <Input
                                    label="Day 1 Start"
                                    type="time"
                                    value={formData.schedule?.day1?.start || ''}
                                    onChange={(e) => {
                                        const currentDay1 = formData.schedule?.day1 || { start: '', end: '' };
                                        setFormData({
                                            ...formData,
                                            schedule: {
                                                ...formData.schedule,
                                                day1: { ...currentDay1, start: e.target.value }
                                            }
                                        });
                                    }}
                                />
                                <Input
                                    label="Day 1 End"
                                    type="time"
                                    value={formData.schedule?.day1?.end || ''}
                                    onChange={(e) => {
                                        const currentDay1 = formData.schedule?.day1 || { start: '', end: '' };
                                        setFormData({
                                            ...formData,
                                            schedule: {
                                                ...formData.schedule,
                                                day1: { ...currentDay1, end: e.target.value }
                                            }
                                        });
                                    }}
                                />
                            </div>
                        )}

                        {(formData.day === 'Day 2' || formData.day === 'Both') && (
                            <div className="grid grid-cols-2 gap-3">
                                <Input
                                    label="Day 2 Start"
                                    type="time"
                                    value={formData.schedule?.day2?.start || ''}
                                    onChange={(e) => {
                                        const currentDay2 = formData.schedule?.day2 || { start: '', end: '' };
                                        setFormData({
                                            ...formData,
                                            schedule: {
                                                ...formData.schedule,
                                                day2: { ...currentDay2, start: e.target.value }
                                            }
                                        });
                                    }}
                                />
                                <Input
                                    label="Day 2 End"
                                    type="time"
                                    value={formData.schedule?.day2?.end || ''}
                                    onChange={(e) => {
                                        const currentDay2 = formData.schedule?.day2 || { start: '', end: '' };
                                        setFormData({
                                            ...formData,
                                            schedule: {
                                                ...formData.schedule,
                                                day2: { ...currentDay2, end: e.target.value }
                                            }
                                        });
                                    }}
                                />
                            </div>
                        )}

                        <Input
                            label="Max Participants"
                            type="number"
                            value={formData.max_participants || 1}
                            onChange={(e) => setFormData({ ...formData, max_participants: Number(e.target.value) })}
                            fullWidth
                        />

                        <div>
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Guidelines
                            </label>
                            <textarea
                                value={formData.guidelines || ''}
                                onChange={(e) => setFormData({ ...formData, guidelines: e.target.value })}
                                className="w-full bg-surface border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm min-h-[120px] focus:border-brand/30 focus:outline-none resize-none font-mono"
                                placeholder="Guidelines (Markdown supported)..."
                            />
                        </div>
                    </div>
                </div>

                {/* Full Width Sections */}
                <div className="mt-4 space-y-4">
                    <ImageUpload
                        label="Cover Image"
                        value={formData.cover_image_url || ''}
                        onChange={(url) => setFormData({ ...formData, cover_image_url: url })}
                    />

                    <div className="border-t border-white/[0.04] pt-4">
                        <h4 className="text-sm font-medium text-white/60 mb-3">Event Manager</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                                label="Manager Name"
                                value={formData.event_manager?.name || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    event_manager: {
                                        name: e.target.value,
                                        phone: formData.event_manager?.phone || ''
                                    }
                                })}
                                placeholder="Manager Name"
                            />
                            <Input
                                label="Manager Phone"
                                value={formData.event_manager?.phone || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    event_manager: {
                                        name: formData.event_manager?.name || '',
                                        phone: e.target.value
                                    }
                                })}
                                placeholder="Manager Phone"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-white/[0.04]">
                    <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEvent}
                        isLoading={actionLoading}
                        disabled={!formData.name || !formData.category}
                    >
                        {selectedEvent ? 'Update Event' : 'Create Event'}
                    </Button>
                </div>
            </Modal>

            {/* Edit Manager Modal */}
            <Modal
                isOpen={isEditManagerModal}
                onClose={() => {
                    setIsEditManagerModal(false);
                    setSelectedEvent(null);
                    setManagerData({ name: '', phone: '' });
                }}
                title="Edit Event Manager"
                size="sm"
            >
                <div className="space-y-4">
                    <div className="bg-brand/[0.05] border border-brand/[0.1] rounded-xl p-3">
                        <p className="text-sm text-white/60">
                            <strong className="text-white">Event:</strong> {selectedEvent?.name}
                        </p>
                    </div>
                    <Input
                        label="Manager Name"
                        value={managerData.name}
                        onChange={(e) => setManagerData({ ...managerData, name: e.target.value })}
                        placeholder="Leave empty for 'None'"
                        fullWidth
                    />
                    <Input
                        label="Phone Number"
                        value={managerData.phone}
                        onChange={(e) => setManagerData({ ...managerData, phone: e.target.value })}
                        placeholder="e.g., +91 98765 43210"
                        fullWidth
                    />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setIsEditManagerModal(false);
                                setSelectedEvent(null);
                                setManagerData({ name: '', phone: '' });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleUpdateManager}
                            isLoading={actionLoading}
                        >
                            Update Manager
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
